import { LinkType } from '@ria-client/segment/enums';
import type { AnalyticsType } from '@ria-client/segment/types';
import { EntityPlacementCtaTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import { TableCell } from '@ria-client/tw-shadcn/components/table';
import { CtaButton, ShortHorizontalLayout, TrackedLink } from '@ria-client/tw-shared-ui';
import { stripFederallyInsuredAnnotation } from '@ria-client/utils/cta-utils';
import Image from 'next/image';
import type { ReactNode } from 'react';
import type { SummaryTableRowWithDrivers } from '../types';
import { DEFAULT_RATING_TOOLTIP } from '../utils';

export interface ProductCellProps {
  beforeMainContent?: ReactNode;
  analyticsPayload: AnalyticsType;
  marketplaceEntity: SummaryTableRowWithDrivers['marketplaceEntity'];
}

export function ProductCell({
  beforeMainContent,
  analyticsPayload,
  marketplaceEntity,
}: ProductCellProps) {
  const {
    cta: originalCta,
    ctaOverride: precomputedCtaOverride,
    displayName,
    federallyInsuredAnnotation,
    image,
    name,
    reviewLink,
    starRating,
    starRatingTooltip,
  } = marketplaceEntity;
  const cta = stripFederallyInsuredAnnotation(
    precomputedCtaOverride ?? originalCta,
    federallyInsuredAnnotation,
  );
  const logo = image?.source ? (
    <Image
      alt={image.alt ?? displayName ?? 'Product logo'}
      height={44}
      src={image.source}
      style={{ objectFit: 'contain' }}
      width={122}
    />
  ) : null;

  return (
    <TableCell className="border-r-[var(--content-neutral-lighter-2)] border-b-[var(--content-neutral-lighter-2)] bg-muted p-0 pt-[var(--cell-padding-top)]">
      {beforeMainContent}
      <div className="flex flex-col items-center justify-center gap-2 px-2 py-3 text-center">
        {logo ? (
          cta ? (
            <TrackedLink
              analyticsPayload={analyticsPayload}
              cta={{ ...cta, type: EntityPlacementCtaTypeEnum.ProductImage }}
              linkType={LinkType.PRODUCT_IMAGE}
              monetizable={analyticsPayload.monetizable}
            >
              {logo}
            </TrackedLink>
          ) : (
            logo
          )
        ) : null}
        {/* Theme/index.ts:1051, legacy mobile product-name fontWeight. */}
        <span className="text-sm font-[325]">{displayName ?? name}</span>
        <ShortHorizontalLayout
          reviewLink={reviewLink}
          starRating={starRating}
          starRatingTooltip={starRatingTooltip ?? DEFAULT_RATING_TOOLTIP}
        />
        {cta ? (
          <div className="flex w-full flex-col items-center gap-2">
            <CtaButton analyticsPayload={analyticsPayload} cta={cta} fullWidth hideSubtext />
            {cta.subtext ? (
              <span className="typography-text-extra-small text-muted-foreground">
                {cta.subtext}
              </span>
            ) : null}
          </div>
        ) : null}
        {cta?.subtext && federallyInsuredAnnotation ? (
          <div className="w-full rounded-[4px] bg-[var(--content-yellow-lighter-2)] px-3 text-center">
            <span className="typography-text-extra-small font-medium text-[var(--content-neutral-darker)]">
              {federallyInsuredAnnotation}
            </span>
          </div>
        ) : null}
      </div>
    </TableCell>
  );
}
